"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Check, ClipboardList, CircleAlert as AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const checklistItems = ["Overall dimensions", "Shape", "Center opening or bull-base area", "Attachment points", "Blower requirement", "Existing patches or previous repairs", "Packed size if transport matters"];
export default function Inflatables() {
  const shouldReduceMotion = useReducedMotion();
  const animationProps = shouldReduceMotion ? {
    initial: {
      opacity: 1,
      y: 0
    },
    animate: {
      opacity: 1,
      y: 0
    }
  } : {
    initial: {
      opacity: 0,
      y: 12
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      amount: 0.2
    },
    transition: {
      duration: 0.3,
      ease: [0.16, 1, 0.3, 1]
    }
  };
  return <section id="inflatablereplacementparts" data-nav="light" className="relative w-full border-b border-border bg-background py-16 text-foreground md:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div {...animationProps} className="space-y-12">
          {/* Top category label / indicator */}
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="rounded-none border-border bg-muted font-mono text-xs uppercase tracking-wider text-muted-foreground">
              Component Specs
            </Badge>
            <div className="h-px flex-1 bg-border" />
          </div>

          {/* Main 2-Column Technical Layout */}
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
            {/* Left Column: Heading and Technical Explanatory Copy */}
            <div className="space-y-6 lg:col-span-7">
              <h2 className="font-serif text-3xl font-bold uppercase tracking-tight text-foreground sm:text-4xl lg:text-5xl">
                Mechanical Bull Inflatable Replacement Parts
              </h2>

              <div className="space-y-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
                <p>
                  The inflatable safety arena is a separate part of the mechanical
                  bull setup, and it may need replacement long before the main
                  machine does. Wear can develop from repeated setup, transport,
                  storage, surface abrasion or previous repairs.
                </p>
                <p>
                  Before choosing a replacement mechanical bull inflatable,
                  compare the dimensions and layout of the existing arena. Also
                  check how the bull base sits within the inflatable and which
                  blower the replacement requires.
                </p>
                <p className="border-l-2 border-primary pl-4 font-medium text-foreground">
                  Do not assume two round or octagonal inflatables are
                  interchangeable simply because they have a similar overall
                  shape.
                </p>
              </div>
            </div>

            {/* Right Column: Industrial Spec Plate Checklist Card */}
            <div className="lg:col-span-5">
              <Card className="rounded-none border border-border bg-card p-0 shadow-sm">
                <CardHeader className="border-b border-border bg-muted/40 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ClipboardList className="size-4 text-primary" />
                      <CardTitle className="font-serif text-xl font-bold uppercase tracking-wide text-card-foreground">
                        Buyer Checklist
                      </CardTitle>
                    </div>
                    <span className="font-mono text-xs text-muted-foreground uppercase">
                      SEC-INF-07
                    </span>
                  </div>
                </CardHeader>

                <CardContent className="px-6 py-6">
                  <ul className="space-y-3">
                    {checklistItems.map((item, i) => <li key={item} data-index={i} className="flex items-start gap-3 border-b border-border/60 pb-3 last:border-b-0 last:pb-0">
                        <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center border border-border bg-muted/50 text-primary">
                          <Check className="size-3.5 stroke-[2.5]" />
                        </span>
                        <div className="flex flex-1 items-baseline justify-between gap-2">
                          <span className="text-sm font-medium text-foreground sm:text-base">
                            {item}
                          </span>
                          <span className="font-mono text-xs text-muted-foreground">
                            {String(i + 1).padStart(2, "0")}
                          </span>
                        </div>
                      </li>)}
                  </ul>

                  <div className="mt-6 flex items-start gap-2.5 bg-muted/30 p-3 border border-border/60">
                    <AlertCircle className="size-4 shrink-0 text-muted-foreground mt-0.5" />
                    <p className="text-xs text-muted-foreground font-mono leading-normal">
                      Confirm base anchoring orientation and blower horsepower
                      before staging shipment.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          <Separator className="bg-border" />

          {/* Plain technical notice with link to inflatables page */}
          <div className="bg-muted/40 border border-border px-5 py-4 text-sm text-muted-foreground">
            <span className="font-mono uppercase text-xs tracking-wider text-foreground mr-2 font-semibold">
              Note:
            </span>
            <span>
              Looking for a complete bull with an inflatable arena? View our{" "}
              <Link to="/InflatableMechanicalBullsPage" className="text-primary underline underline-offset-4 hover:text-primary/80 transition-colors">
                Inflatable Mechanical Bulls page
              </Link>
              .
            </span>
          </div>
        </motion.div>
      </div>
    </section>;
}